import { ROUTES } from '#/config/routes';
import { formatDocumentCurrencyLabel } from '#/utils/documentCurrencyLabel';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAsyncMultiFilterMeta,
  createDateFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import CustomerOrderStatusBadge from '#/modules/crmMgmt/customerOrder/components/CustomerOrderStatusBadge';
import {
  readCustomerOrderDateRangeFilter,
  resolveCustomerOrderDateFilterChipDisplay,
} from '#/modules/crmMgmt/customerOrder/helper/customerOrderDateListFilter';
import { CUSTOMER_ORDER_STATUS_FILTER_OPTIONS } from '#/modules/crmMgmt/customerOrder/helper/customerOrderStatus';
import { searchCustomerFilterOptions } from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import { formatDateOrDash } from '#/utils/date';
import { formatDisplayAmount } from '#/utils/formatNumber';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return value;
}

function formatCustomerLabel(customer) {
  if (!customer) {
    return '-';
  }
  const label = [customer.code, customer.name].filter(Boolean).join(' - ');
  return label || '-';
}

export function buildCustomerOrderColumns({
  filters = {},
  onFilterChange,
  onCustomerOrderDateRangeChange,
  customerLabelCache,
  currencyOptions = [],
  canShowEdit = (row) => row.status === 'DRAFT',
} = {}) {
  return [
    createDocumentNavLinksColumn({
      getDetailPath: (row) => ROUTES.customerOrderDetail(row.id),
      getEditPath: (row) => ROUTES.customerOrderEdit(row.id),
      canShowEdit,
      size: 80,
      minSize: 80,
    }),
    {
      id: 'customerOrderNo',
      header: '客戶訂單號',
      accessorKey: 'customerOrderNo',
      accessorFn: (row) => displayDash(row.customerOrderNo),
      size: 140,
      enableColumnFilter: true,
      meta: createTextFilterMeta({
        key: 'customerOrderNo',
        label: '客戶訂單號',
        filterValue: filters.customerOrderNo,
        onChange: (value) => onFilterChange('customerOrderNo', value),
        placeholder: '搜尋客戶訂單號',
      }),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      size: 110,
      // BE sort 白名單：customer_order_no、customer_order_date、updated_at
      enableSorting: false,
      cell: ({ row }) => (
        row.original.status
          ? <CustomerOrderStatusBadge status={row.original.status} size="sm" />
          : '-'
      ),
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: CUSTOMER_ORDER_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
    },
    {
      id: 'customer',
      header: '客戶',
      accessorFn: (row) => formatCustomerLabel(row.customer),
      size: 160,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createAsyncMultiFilterMeta({
          key: 'customerId',
          label: '客戶',
          searchOptions: searchCustomerFilterOptions,
          filterValue: filters.customerId,
          onChange: (value) => onFilterChange('customerId', value),
          resolveFilterDisplay: (values) => (
            resolveFromLabelCache(customerLabelCache, values)
          ),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(customerLabelCache, options, values);
          },
        }),
      },
    },
    {
      id: 'customerOrderDate',
      header: '訂單日',
      accessorKey: 'customerOrderDate',
      accessorFn: (row) => formatDateOrDash(row.customerOrderDate),
      size: 148,
      minSize: 148,
      enableColumnFilter: true,
      meta: createDateFilterMeta({
        key: 'customerOrderDateFrom',
        label: '訂單日',
        mode: 'range',
        filterValue: readCustomerOrderDateRangeFilter(filters),
        onChange: onCustomerOrderDateRangeChange,
        placeholder: '西元年/月/日',
        resolveFilterDisplay: resolveCustomerOrderDateFilterChipDisplay,
      }),
    },
    {
      id: 'currencyCode',
      header: '幣別',
      accessorKey: 'currencyCode',
      accessorFn: (row) => formatDocumentCurrencyLabel(row),
      size: 80,
      enableSorting: false,
      enableColumnFilter: true,
      meta: createFixedMultiFilterMeta({
        key: 'currencyId',
        label: '幣別',
        options: currencyOptions,
        filterValue: filters.currencyId,
        onChange: (value) => onFilterChange('currencyId', value),
      }),
    },
    {
      id: 'totalAmount',
      header: '總額',
      accessorKey: 'totalAmount',
      // Search 精簡 projection 可不回金額／amountSummary；缺則 format 為 —
      accessorFn: (row) => formatDisplayAmount(row.totalAmount),
      size: 110,
      enableSorting: false,
      meta: {
        cellClassName: 'td_right',
      },
    },
    {
      id: 'lineCount',
      header: '明細數',
      accessorKey: 'lineCount',
      accessorFn: (row) => displayDash(row.lineCount),
      size: 80,
      enableSorting: false,
    },
    {
      id: 'salesOrderCount',
      header: '銷貨單數',
      accessorKey: 'salesOrderCount',
      accessorFn: (row) => displayDash(row.salesOrderCount),
      size: 90,
      enableSorting: false,
    },
    {
      id: 'issueReason',
      header: '事由',
      accessorKey: 'issueReason',
      accessorFn: (row) => displayDash(row.issueReason),
      size: 160,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'issueReason',
          label: '事由',
          filterValue: filters.issueReason,
          onChange: (value) => onFilterChange('issueReason', value),
          placeholder: '搜尋事由',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.issueReason)} />
      ),
    },
    {
      id: 'remark',
      header: '備註',
      accessorKey: 'remark',
      accessorFn: (row) => displayDash(row.remark),
      size: 180,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'remark',
          label: '備註',
          filterValue: filters.remark,
          onChange: (value) => onFilterChange('remark', value),
          placeholder: '搜尋備註',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.remark)} />
      ),
    },
  ];
}
